import React, { useEffect, useState } from 'react';
import client, { apiError } from '../api/client';

export default function MockExams() {
  const [exams, setExams] = useState([]);
  const [active, setActive] = useState(null); // { key, title, questions }
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const loadList = async () => {
    try {
      const [{ data: list }, { data: hist }] = await Promise.all([
        client.get('/mock-exams'),
        client.get('/mock-exams/history/me'),
      ]);
      setExams(list.exams);
      setHistory(hist.attempts);
    } catch (err) {
      setError(apiError(err));
    }
  };

  useEffect(() => { loadList(); }, []);

  const startExam = async (key) => {
    setError(''); setResult(null); setAnswers({}); setLoading(true);
    try {
      const { data } = await client.get(`/mock-exams/${key}`);
      setActive(data);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const submit = async () => {
    setLoading(true); setError('');
    try {
      const { data } = await client.post(`/mock-exams/${active.key}/submit`, { answers });
      setResult(data);
      loadList();
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setActive(null); setResult(null); setAnswers({}); };

  // Result review view
  if (active && result) {
    const byId = Object.fromEntries(active.questions.map((q) => [q.id, q]));
    return (
      <div className="container" style={{ marginTop: 28, marginBottom: 40 }}>
        <div className="card card-pad-lg">
          <h1>Results — {active.title}</h1>
          <p className="muted">
            You scored <b>{result.correct}/{result.total}</b> ({result.scorePercent}%).
          </p>
          <div className="divider" />
          <div className="stack">
            {result.review.map((r, i) => {
              const q = byId[r.id];
              return (
                <div key={r.id} className="card" style={{ borderColor: r.isCorrect ? '#b7e6cd' : '#f5c2c2' }}>
                  <div className="small" style={{ fontWeight: 600 }}>{i + 1}. {q.prompt}</div>
                  <div className="small" style={{ marginTop: 8 }}>
                    Your answer: <b>{r.selected || '—'}</b> · Correct: <b>{r.answer}</b>{' '}
                    <span className={`status ${r.isCorrect ? 'active' : 'lapsed'}`}>
                      {r.isCorrect ? 'Correct' : 'Incorrect'}
                    </span>
                  </div>
                  {r.explanation && <p className="tiny muted" style={{ marginTop: 8 }}>{r.explanation}</p>}
                </div>
              );
            })}
          </div>
          <button className="btn btn-primary" style={{ marginTop: 18 }} onClick={reset}>Back to mock exams</button>
        </div>
      </div>
    );
  }

  // Taking an exam
  if (active) {
    const answered = Object.keys(answers).length;
    return (
      <div className="container" style={{ marginTop: 28, marginBottom: 40 }}>
        <div className="row-between wrap" style={{ marginBottom: 14 }}>
          <h1>{active.title}</h1>
          <button className="btn btn-ghost btn-sm" onClick={reset}>Cancel</button>
        </div>
        {active.description && <p className="muted small">{active.description}</p>}
        {error && <div className="alert alert-error" style={{ margin: '12px 0' }}>{error}</div>}
        <div className="stack">
          {active.questions.map((q, i) => (
            <div className="card" key={q.id}>
              <div className="small" style={{ fontWeight: 600 }}>{i + 1}. {q.prompt}</div>
              <div className="stack" style={{ marginTop: 10 }}>
                {q.options.map((o) => (
                  <label key={o.key} className="row" style={{ cursor: 'pointer', fontWeight: 400 }}>
                    <input
                      type="radio"
                      style={{ width: 'auto' }}
                      name={q.id}
                      checked={answers[q.id] === o.key}
                      onChange={() => setAnswers({ ...answers, [q.id]: o.key })}
                    />
                    <span className="small"><b>{o.key}.</b> {o.text}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="row-between" style={{ marginTop: 18 }}>
          <span className="muted small">{answered}/{active.questions.length} answered</span>
          <button className="btn btn-primary" onClick={submit} disabled={loading}>
            {loading ? <span className="spinner" /> : 'Submit exam'}
          </button>
        </div>
      </div>
    );
  }

  // Exam list
  return (
    <div className="container" style={{ marginTop: 28, marginBottom: 40 }}>
      <h1>Mock exams</h1>
      <p className="muted small">Original, ECO-aligned practice sets. Timed judgement, not memorisation.</p>
      {error && <div className="alert alert-error" style={{ margin: '12px 0' }}>{error}</div>}
      <div className="grid-2" style={{ marginTop: 18 }}>
        {exams.length === 0 && <div className="muted small">No mock exams available yet.</div>}
        {exams.map((ex) => (
          <div className="card" key={ex.key}>
            <span className="pill">{ex.questionCount} questions</span>
            <h3 style={{ marginTop: 12 }}>{ex.title}</h3>
            <p className="muted small">{ex.description}</p>
            <button className="btn btn-primary btn-sm" onClick={() => startExam(ex.key)} disabled={loading}>
              Start
            </button>
          </div>
        ))}
      </div>

      {history.length > 0 && (
        <div className="card" style={{ marginTop: 24 }}>
          <h3>Your recent attempts</h3>
          <table>
            <thead>
              <tr><th>Exam</th><th>Score</th><th>When</th></tr>
            </thead>
            <tbody>
              {history.slice(0, 8).map((a) => (
                <tr key={a.id}>
                  <td className="small">{a.examTitle}</td>
                  <td className="small"><b>{a.scorePercent}%</b> ({a.correctCount}/{a.totalQuestions})</td>
                  <td className="tiny muted">{new Date(a.createdAt).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
